import { useEffect, useRef, useState } from 'react';
import { toast } from '@/components/toast/toast';
import { Certificate } from '../../../../entities/certificate';
import { getUserCertificates } from '../../../../use-cases/get-user-signatures';
import styles from './sign-document-modal.module.scss';
import { UploadedDocumentData } from '../../../../use-cases/upload-document';
import { Button } from '@/components/ui/button';
import {
	Select,
	SelectContent,
	SelectItem,
	SelectTrigger,
	SelectValue,
} from '@/components/ui/select';
import { Loader2Icon } from 'lucide-react';
import notifyHB from '@/config/notifyHB';
import { PinSignInput } from '@/components/pin-sign-input/pin-sign-input';
import { signUploadedDocument } from '@/use-cases/sign-uploaded-document';
import {
	buildLineageOutcomeSentences,
	LineageOutcome,
} from '../../util/lineage-outcome';
import {
	Dialog,
	DialogClose,
	DialogContent,
	DialogFooter,
	DialogTitle,
	DialogTrigger,
} from '@/components/ui/dialog';

type SignDocumentModalProps = {
	isOpen: boolean;
	onSign: (
		documentUlid: string,
		certificateId: Certificate['certificate_id'],
		otp: string,
		otpType: Parameters<typeof signUploadedDocument>[5],
	) => Promise<LineageOutcome | null | undefined>;
	onCertificateSelect: (
		certificateId: Certificate['certificate_id'],
	) => Promise<Record<string, unknown>>;
	uploadDocument: (
		selectedCertificateSerial: Certificate['certificate_id'],
	) => Promise<UploadedDocumentData | undefined>;
	onClose: () => void;
	onOpen: () => void;
};

export const SignDocumentModal = (props: SignDocumentModalProps) => {
	const [certificates, setCertificates] = useState<Certificate[]>([]);
	const [selectedCertificate, setSelectedCertificate] = useState<
		Certificate['certificate_id'] | null
	>(null);
	const [isFetchingCertificates, setIsFetchingCertificates] = useState(false);
	const [isSigning, setIsSigning] = useState(false);
	const [isSigned, setIsSigned] = useState(true);
	const [otp, setOtp] = useState('');
	const [lineageOutcome, setLineageOutcome] = useState<LineageOutcome | null>(
		null,
	);
	const uploadedDocumentData = useRef<UploadedDocumentData | null>(null);

	const handleSign = () => {
		if (selectedCertificate && otp && uploadedDocumentData.current) {
			setIsSigning(true);
			props
				.onSign(
					uploadedDocumentData.current.docUlid,
					selectedCertificate,
					otp,
					'pin',
				)
				.then((outcome) => {
					setLineageOutcome(outcome ?? null);
					setIsSigned(true);
				})
				.catch(() => {})
				.finally(() => {
					setIsSigning(false);
					// props.onClose();
				});
		}
	};

	useEffect(() => {
		if (props.isOpen) {
			setIsSigned(false);
			setLineageOutcome(null);
			setIsFetchingCertificates(true);
			getUserCertificates()
				.then((serverCertificates) => {
					if (serverCertificates) {
						setCertificates(
							serverCertificates.filter(
								(certificate) =>
									certificate.status === 'Active',
							),
						);
					}
				})
				.catch((error) => {
					toast({
						mode: 'destructive',
						description:
							'No fue posible subir el documento. Intenta de nuevo más tarde',
					});
					console.debug(error);
					notifyHB('sign-document', 'sign-document-modal', error);
				})
				.finally(() => {
					setIsFetchingCertificates(false);
				});
		} else {
			setSelectedCertificate(null);
		}
	}, [props.isOpen]);

	useEffect(() => {
		if (selectedCertificate) {
			props
				.uploadDocument(selectedCertificate)
				.then((uploadedDocument) => {
					if (uploadedDocument) {
						uploadedDocumentData.current = uploadedDocument;
					}
				})
				.catch((error) =>
					notifyHB(
						'sign-document',
						'SignDocumentModal',
						`Fail to upload document with ${error}`,
					),
				);
		}
	}, [selectedCertificate]);

	useEffect(() => {
		if (!selectedCertificate) {
			setOtp('');
		}
	}, [selectedCertificate]);

	return (
		<Dialog
			open={props.isOpen}
			onOpenChange={(isOpen) =>
				isOpen ? props.onOpen() : props.onClose()
			}
		>
			<DialogTrigger asChild>
				<Button variant="default" data-tour="annotation-send-btn">
					Firmar
				</Button>
			</DialogTrigger>
			<DialogContent
				aria-description="Dialog to select a certificate"
				aria-describedby={undefined}
				className={styles['sign-document-modal']}
			>
				{isSigned ? (
					<>
						<DialogTitle className={styles.title}>
							¡Documento firmado!
						</DialogTitle>
						<img
							className={styles['signed-document-image']}
							src="/signed-document.svg"
						/>
						<span className={styles['signed-text']}>
							Puedes encontrarlo en tus descargas
						</span>
						{lineageOutcome && (
							<div
								className={styles['lineage-outcome']}
								data-testid="lineage-outcome"
							>
								{buildLineageOutcomeSentences(
									lineageOutcome,
								).map((sentence, index) => (
									<p
										key={sentence}
										className={
											styles['lineage-outcome-sentence']
										}
										data-testid={
											index === 0
												? 'lineage-page-outcome'
												: 'public-retrieval-outcome'
										}
									>
										{sentence}
									</p>
								))}
								{lineageOutcome.verifyUrl && (
									<a
										className={styles['lineage-link']}
										href={lineageOutcome.verifyUrl}
										target="_blank"
										rel="noreferrer noopener"
									>
										{lineageOutcome.verifyUrl}
									</a>
								)}
							</div>
						)}
						<DialogClose asChild>
							<Button>Cerrar</Button>
						</DialogClose>
					</>
				) : (
					<>
						<DialogTitle className={styles.title}>
							Selecciona un certificado
						</DialogTitle>
						<div className={styles['input-group']}>
							¿Qué certificado quieres usar?
							<Select
								value={selectedCertificate ?? ''}
								onValueChange={setSelectedCertificate}
								disabled={isFetchingCertificates}
							>
								<SelectTrigger className="w-[180px]">
									<SelectValue placeholder="Selecciona un certificado para firmar" />
									{isFetchingCertificates && (
										<Loader2Icon className="animate-spin" />
									)}
								</SelectTrigger>
								<SelectContent>
									{certificates.map((certificate) => (
										<SelectItem
											key={certificate.certificate_id}
											value={certificate.certificate_id}
										>
											{certificate.name}
										</SelectItem>
									))}
								</SelectContent>
							</Select>
						</div>
						<div className={styles['otp']}>
							{selectedCertificate !== null && (
								<div className={styles['otp-accordion']}>
									<PinSignInput
										value={otp}
										setValue={setOtp}
										disabled={isSigning}
										autoFocus
									/>
								</div>
							)}
						</div>
						<DialogFooter>
							<DialogClose asChild>
								<Button
									variant="secondary"
									disabled={isSigning}
								>
									Cancelar
								</Button>
							</DialogClose>
							<Button
								variant="default"
								onClick={handleSign}
								disabled={
									!selectedCertificate || !otp || isSigning
								}
							>
								Firmar
								{isSigning && (
									<Loader2Icon className="animate-spin" />
								)}
							</Button>
						</DialogFooter>
					</>
				)}
			</DialogContent>
		</Dialog>
	);
};
